import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, ExternalLink, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChatInterface } from "@/components/chat/ChatInterface";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import {
  fetchLeadConversationForChat,
  type LeadConversationForChat,
} from "@/lib/fetch-lead-conversation-for-chat";
import { markConversationRead } from "@/lib/conversation-member-read";
import { useConversationChatControls } from "@/hooks/useConversationChatControls";
import { ConversationIaToggle } from "@/components/conversations/ConversationChatHeaderActions";
import type { Lead } from "@/types/lead";

const AGENT_BLOCKED_RESUME_TOAST = {
  title: "IA bloqueada neste estágio",
  description:
    "O lead está em um estágio configurado para desligar a IA. Mova o lead para outro estágio antes de reativar.",
  variant: "destructive" as const,
};

interface LeadQuickChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  workspaceId: string | null;
}

export function LeadQuickChatSheet({
  open,
  onOpenChange,
  lead,
  workspaceId,
}: LeadQuickChatSheetProps) {
  const { memberId, memberDisplayName } = useMemberPermissions();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<LeadConversationForChat | null>(null);
  const [agentResumeBlocked, setAgentResumeBlocked] = useState(false);

  const patchConversation = useCallback(
    (updater: (prev: LeadConversationForChat) => LeadConversationForChat) => {
      setConversation((prev) => (prev ? updater(prev) : prev));
    },
    [],
  );

  const controls = useConversationChatControls({
    workspaceId,
    memberId,
    memberDisplayName,
    conversation,
    patchConversation,
  });

  useEffect(() => {
    if (!open || !lead?.id || !workspaceId) {
      setConversation(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const row = await fetchLeadConversationForChat(supabase, workspaceId, lead.id, {
          name: lead.name,
          phone: lead.phone,
          metadata: lead.metadata,
        });
        if (cancelled) return;
        setConversation(row);
        if (row?.conversation_id) {
          void markConversationRead(row.conversation_id);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast({
            title: "Erro ao carregar conversa",
            description: "Não foi possível abrir o chat deste lead.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, lead?.id, lead?.name, lead?.phone, lead?.metadata, workspaceId, toast]);

  useEffect(() => {
    if (!open || !lead?.id) {
      setAgentResumeBlocked(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.rpc("lead_has_agent_disabled_stage", {
        p_lead_id: lead.id,
      });
      if (cancelled) return;
      setAgentResumeBlocked(!error && data === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, lead?.id, conversation?.state]);

  const leadId = lead?.id ?? "";
  const leadName = lead?.name ?? "Lead";

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md md:max-w-lg"
        >
          <SheetHeader className="shrink-0 border-b px-4 py-3 text-left">
            <div className="flex items-start justify-between gap-2 pr-8">
              <div className="min-w-0">
                <SheetTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate">{leadName}</span>
                </SheetTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Conversa do lead</p>
              </div>
              {conversation ? (
                <div className="flex shrink-0 items-center gap-2">
                  <ConversationIaToggle
                    conversation={{
                      conversation_id: conversation.conversation_id,
                      lead_id: conversation.lead_id,
                      lead_name: conversation.lead_name,
                      channel: conversation.channel,
                      state: conversation.state,
                      followup_enabled: conversation.followup_enabled,
                      is_closed: conversation.is_closed,
                    }}
                    agentResumeBlocked={agentResumeBlocked}
                    onPause={controls.handlePauseConversation}
                    onResume={(id) =>
                      void controls.handleResumeConversation(id, conversation.lead_id)
                    }
                    onAgentResumeBlocked={() => toast(AGENT_BLOCKED_RESUME_TOAST)}
                  />
                  <Button variant="ghost" size="sm" className="shrink-0 h-8" asChild>
                    <Link
                      to={`/conversations?lead_id=${encodeURIComponent(leadId)}`}
                      onClick={() => onOpenChange(false)}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Abrir inbox
                    </Link>
                  </Button>
                </div>
              ) : null}
            </div>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {loading ? (
              <div className="flex flex-1 items-center justify-center text-muted-foreground gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Carregando mensagens…</span>
              </div>
            ) : !conversation ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
                <Alert className="text-left">
                  <AlertDescription>
                    Este lead ainda não tem conversa registrada. Inicie pelo menu Conversas ou pelo
                    WhatsApp vinculado.
                  </AlertDescription>
                </Alert>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/conversations">Ir para conversas</Link>
                </Button>
              </div>
            ) : workspaceId ? (
              <ChatInterface
                workspaceId={workspaceId}
                leadId={conversation.lead_id}
                leadName={conversation.lead_name}
                leadMetadata={conversation.metadata}
                conversations={[
                  {
                    channel: conversation.channel,
                    conversation_id: conversation.conversation_id,
                    state: conversation.state,
                    followup_enabled: conversation.followup_enabled,
                    integration_id: conversation.integration_id,
                  },
                ]}
                onPauseConversation={controls.handlePauseConversation}
                onResumeConversation={controls.handleResumeConversation}
                onCloseConversation={controls.handleCloseConversation}
                onToggleFollowup={controls.handleToggleFollowup}
                onClearContext={async (id: string) => { await controls.handleClearContext(id); }}
                clearMessagesSignal={controls.clearMessagesSignal}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={controls.clearContextDialogOpen}
        onOpenChange={controls.setClearContextDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar contexto?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as mensagens desta conversa serão removidas permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => void controls.confirmClearContext()}
            >
              Limpar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
